import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useRetailerAuth } from "../../context/RetailerAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { 
  SendHorizontal, ClipboardList, Building2, TrendingUp,
  Clock, CheckCircle2, AlertCircle, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RetailerDashboard() {
  const { retailer } = useRetailerAuth();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/retailer/stats`),
        axios.get(`${API}/retailer/jobs`)
      ]);
      setStats(statsRes.data);
      setRecentJobs(jobsRes.data.jobs.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      received: "bg-blue-100 text-blue-700",
      diagnosed: "bg-purple-100 text-purple-700",
      waiting_approval: "bg-yellow-100 text-yellow-700",
      in_repair: "bg-orange-100 text-orange-700",
      completed: "bg-green-100 text-green-700",
      delivered: "bg-emerald-100 text-emerald-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome, {retailer?.name}</h1>
          <p className="text-gray-600">{retailer?.shop_name}</p>
        </div>
        <Link to="/retailer/forward-job">
          <Button className="bg-orange-600 hover:bg-orange-700">
            <SendHorizontal className="w-4 h-4 mr-2" />
            Forward New Job
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_jobs || 0}</p>
                <p className="text-xs text-gray-500">Total Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.active_jobs || 0}</p>
                <p className="text-xs text-gray-500">Active Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.completed_jobs || 0}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.partner_shops || 0}</p>
                <p className="text-xs text-gray-500">Partner Shops</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions + Recent Jobs */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/retailer/forward-job" className="block">
              <Button variant="outline" className="w-full justify-start hover:bg-orange-50 hover:border-orange-200">
                <SendHorizontal className="w-4 h-4 mr-3 text-orange-600" />
                Forward New Job
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/retailer/partners" className="block">
              <Button variant="outline" className="w-full justify-start hover:bg-purple-50 hover:border-purple-200">
                <Building2 className="w-4 h-4 mr-3 text-purple-600" />
                Add Partner Shop
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/retailer/jobs" className="block">
              <Button variant="outline" className="w-full justify-start hover:bg-blue-50 hover:border-blue-200">
                <ClipboardList className="w-4 h-4 mr-3 text-blue-600" />
                View All Jobs
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Jobs */}
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Jobs</CardTitle>
            <Link to="/retailer/jobs">
              <Button variant="ghost" size="sm" className="text-orange-600">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentJobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No jobs forwarded yet</p>
                <Link to="/retailer/forward-job">
                  <Button className="mt-4 bg-orange-600 hover:bg-orange-700">
                    Forward Your First Job
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentJobs.map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{job.customer_name}</p>
                      <p className="text-sm text-gray-500">
                        {job.device_brand} {job.device_model} • {job.repair_shop_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={getStatusColor(job.status)}>
                        {job.status.replace(/_/g, " ")}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">{job.job_number}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* This Month Stats */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            This Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{stats?.this_month || 0}</p>
              <p className="text-sm text-gray-500">Jobs Forwarded</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{stats?.status_breakdown?.completed || 0}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{stats?.status_breakdown?.in_repair || 0}</p>
              <p className="text-sm text-gray-500">In Repair</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{stats?.status_breakdown?.waiting_approval || 0}</p>
              <p className="text-sm text-gray-500">Awaiting Approval</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
